/** Thai subdistricts from thai-address-universal, searchable by any part of the address */
import type { ThaiArea } from '@tnn/core'

export type Area = Required<{ [K in keyof ThaiArea]: string }>
type AreaRow = Area & { text: string }

let dataset: Promise<AreaRow[]> | undefined

/** Every subdistrict in Thailand (about 7,400 rows), loaded on first use and kept */
export function thaiAreas(): Promise<AreaRow[]> {
  dataset ??= import('thai-address-universal').then(async (lib) => {
    await lib.getProvinceAll() // loads the Thai dataset
    return lib.getDatabase().map((r) => ({
      subdistrict: r.sub_district,
      district: r.district,
      province: r.province,
      postcode: r.postal_code,
      text: `${r.sub_district} ${r.district} ${r.province} ${r.postal_code}`,
    }))
  })
  return dataset
}

const PREFIX = /^(ตำบล|อำเภอ|จังหวัด|แขวง|เขต|ต\.|อ\.|จ\.)/

/**
 * How well the typed words name this place: each word takes the field it fits
 * best (exact 3, prefix 2, inside 1), and a field is used once, so
 * "ท่าศาลา ท่าศาลา" ranks ต.ท่าศาลา อ.ท่าศาลา above ต.ท่าศาลา elsewhere.
 */
function score(r: AreaRow, words: string[]): number {
  const fields = [r.subdistrict, r.district, r.province, r.postcode]
  const used = new Set<number>()
  let total = 0
  for (const w of words) {
    let best = -1
    let bestScore = 0
    fields.forEach((f, i) => {
      const s = used.has(i) ? 0 : f === w ? 3 : f.startsWith(w) ? 2 : f.includes(w) ? 1 : 0
      if (s > bestScore) {
        best = i
        bestScore = s
      }
    })
    if (best >= 0) used.add(best)
    total += bestScore
  }
  return total
}

/** "ท่าศาลา นครศรี", "อ.ทุ่งสง" or "80160": every word must appear; the best fits come first */
export async function searchAreas(query: string, limit = 20): Promise<Area[]> {
  const words = query
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(PREFIX, ''))
    .filter(Boolean)
  if (!words.length) return []
  const rows = (await thaiAreas()).filter((r) => words.every((w) => r.text.includes(w)))
  return rows
    .map((r) => [score(r, words), r] as const)
    .sort((a, b) => b[0] - a[0])
    .slice(0, limit)
    .map(([, { text, ...area }]) => area)
}
